import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Plus,
  KeyRound,
  Trash2,
  Edit2,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  Search,
  Lock,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { rolesService, adminService } from '@services/roles.service';
import { permissionsService } from '@services/permissions.service';
import type { Role, Permission } from '@apptypes/index';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';
import { usePermission } from '@hooks/usePermission';

// ── Create / Edit modal ───────────────────────────────────────────────────────

function RoleFormModal({
  role,
  onClose,
}: {
  role?: Role;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');

  const createMutation = useMutation({
    mutationFn: () => rolesService.create({ name, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => rolesService.update(role!.roleId, { name, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            {role ? 'Edit Role' : 'Create Role'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Operations Manager"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this role for?"
              rows={3}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
            />
          </div>

          {error && (
            <p className="text-[13px] text-[var(--state-danger)]">
              {(error as any)?.message ?? 'Something went wrong.'}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[var(--border-subtle)]">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => role ? updateMutation.mutate() : createMutation.mutate()}
            disabled={!name.trim() || isPending}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {isPending ? 'Saving...' : role ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Permissions panel ─────────────────────────────────────────────────────────

function RolePermissionsPanel({ role, isSystemRole }: { role: Role; isSystemRole: boolean }) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('roles.update');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(role.permissions.map((p) => p.permissionId))
  );
  const [dirty, setDirty] = useState(false);

  const { data: allPerms = [], isLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionsService.list(),
  });

  const saveMutation = useMutation({
    mutationFn: () => rolesService.setPermissions(role.roleId, Array.from(selected)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setDirty(false);
    },
  });

  const toggle = (permId: string) => {
    if (!canEdit) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
    setDirty(true);
  };

  const filtered = allPerms.filter(
    (p) => !search || p.code.includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase())
  );
  const grouped = filtered.reduce<Record<string, Permission[]>>((acc, p) => {
    const mod = p.code.split('.')[0] ?? 'other';
    if (!acc[mod]) acc[mod] = [];
    acc[mod].push(p);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="px-6 py-4 text-[13px] text-[var(--text-muted)]">
        Loading permissions...
      </div>
    );
  }

  return (
    <div className="border-t border-[var(--border-subtle)] bg-[var(--bg-base)]">
      {isSystemRole && (
        <div className="px-6 pt-4 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-[var(--state-warning)]" strokeWidth={1.5} />
          <span className="text-[12px] text-[var(--state-warning)] font-medium">
            Editing system role: changes affect all users with this role
          </span>
        </div>
      )}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[13px] font-semibold text-[var(--text-primary)]">
            Assign Permissions
            <span className="ml-2 text-[12px] text-[var(--text-muted)] font-normal">
              {selected.size} selected
            </span>
          </p>
          {dirty && canEdit && (
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-forest)] px-4 py-1.5 text-[12px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>

        {saveMutation.error && (
          <p className="text-[12px] text-[var(--state-danger)] mb-2">
            {(saveMutation.error as any)?.message ?? 'Failed to save.'}
          </p>
        )}

        <div className="relative mb-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permissions..."
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
          />
        </div>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([mod, perms]) => {
            const allSelected = perms.every((p) => selected.has(p.permissionId));
            const someSelected = perms.some((p) => selected.has(p.permissionId));
            return (
              <div key={mod} className="border border-[var(--border-subtle)] rounded-2xl overflow-hidden bg-white">
                <button
                  onClick={() => {
                    if (!canEdit) return;
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (allSelected) perms.forEach((p) => next.delete(p.permissionId));
                      else perms.forEach((p) => next.add(p.permissionId));
                      return next;
                    });
                    setDirty(true);
                  }}
                  disabled={!canEdit}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-2.5 bg-[var(--bg-base)] transition-colors',
                    canEdit ? 'hover:bg-[var(--bg-elevated)]' : 'cursor-default'
                  )}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    {mod}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-[var(--text-muted)]">
                      {perms.filter((p) => selected.has(p.permissionId)).length}/{perms.length}
                    </span>
                    <div className={cn(
                      'w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
                      allSelected
                        ? 'bg-[var(--gradient-to)] border-[var(--gradient-to)]'
                        : someSelected
                        ? 'bg-[var(--bg-elevated)] border-[var(--gradient-to)]'
                        : 'border-[var(--border-default)]'
                    )}>
                      {(allSelected || someSelected) && (
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={1.5} />
                      )}
                    </div>
                  </div>
                </button>
                <div className="px-4 py-2.5 flex flex-wrap gap-1.5">
                  {perms.map((p) => (
                    <button
                      key={p.permissionId}
                      onClick={() => toggle(p.permissionId)}
                      disabled={!canEdit}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-mono font-semibold transition-colors',
                        selected.has(p.permissionId)
                          ? 'bg-[var(--bg-elevated)] text-[var(--text-accent)] border border-[var(--gradient-to)]/30'
                          : 'bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border-subtle)]',
                        canEdit && !selected.has(p.permissionId) && 'hover:border-[var(--border-default)]',
                        !canEdit && 'cursor-default'
                      )}
                    >
                      {selected.has(p.permissionId) && (
                        <Check className="w-2.5 h-2.5" strokeWidth={1.5} />
                      )}
                      {p.code.split('.').slice(1).join('.')}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const RolesListPage = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: roles = [], isLoading, error } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesService.list(),
  });

  const { data: superAdminData } = useQuery({
    queryKey: ['me-super-admin'],
    queryFn: () => adminService.checkSuperAdmin(),
  });
  const isSuperAdmin = superAdminData?.superAdmin ?? false;

  const deleteMutation = useMutation({
    mutationFn: (roleId: string) => rolesService.delete(roleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });

  const toggleExpand = (roleId: string) =>
    setExpandedId((prev) => (prev === roleId ? null : roleId));

  const canExpand = (role: Role) => !role.isSystemRole || isSuperAdmin;

  return (
    <PageGuard permission="roles.read">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
              Access Control
            </p>
            <h2 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Roles
            </h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-1">
              Chekit-level roles with their permission sets
            </p>
          </div>
          <PermissionGate permission="roles.create">
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Create Role
            </button>
          </PermissionGate>
        </div>

        {/* Roles table */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          {isLoading && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              Loading roles...
            </div>
          )}
          {error && (
            <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
              Failed to load roles.
            </div>
          )}

          {!isLoading && !error && roles.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              No roles defined yet.
            </div>
          )}

          {!isLoading && !error && roles.length > 0 && (
            <>
              {/* Table header */}
              <div className="bg-[var(--bg-base)] px-6 py-3 flex items-center justify-between border-b border-[var(--border-subtle)]">
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Role
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Permissions
                </span>
              </div>

              <div className="divide-y divide-[var(--border-subtle)]">
                {roles.map((role) => (
                  <div key={role.roleId}>
                    <div className="flex items-center justify-between px-6 py-4 hover:bg-[var(--bg-base)] transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <button
                          onClick={() => canExpand(role) && toggleExpand(role.roleId)}
                          className={cn(
                            'flex items-center gap-3 flex-1 min-w-0 text-left',
                            !canExpand(role) && 'cursor-default'
                          )}
                        >
                          <Shield
                            className={cn(
                              'w-5 h-5 shrink-0',
                              role.isSystemRole ? 'text-[var(--text-accent)]' : 'text-[var(--text-muted)]'
                            )}
                            strokeWidth={1.5}
                          />
                          <div className="min-w-0">
                            <div className="text-[14px] font-semibold text-[var(--text-primary)] flex items-center gap-2 flex-wrap">
                              {role.name}
                              {role.isSystemRole && (
                                <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[var(--bg-elevated)] text-[var(--text-accent)]">
                                  System
                                </span>
                              )}
                            </div>
                            {role.description && (
                              <div className="text-[13px] text-[var(--text-muted)] truncate mt-0.5">
                                {role.description}
                              </div>
                            )}
                          </div>
                        </button>
                      </div>

                      <div className="flex items-center gap-2 ml-4 shrink-0">
                        <span className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)]">
                          <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                          {role.permissions.length}
                        </span>

                        {canExpand(role) && (
                          <button
                            onClick={() => toggleExpand(role.roleId)}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
                            title="Manage permissions"
                          >
                            {expandedId === role.roleId
                              ? <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                              : <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                            }
                          </button>
                        )}

                        {!role.isSystemRole && (
                          <>
                            <PermissionGate permission="roles.update">
                              <button
                                onClick={() => setEditingRole(role)}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
                                title="Edit role"
                              >
                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="roles.delete">
                              <button
                                onClick={() => {
                                  if (confirm(`Delete role "${role.name}"? This cannot be undone.`)) {
                                    deleteMutation.mutate(role.roleId);
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--state-danger-bg)] hover:text-[var(--state-danger)] transition-colors disabled:opacity-50"
                                title="Delete role"
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </PermissionGate>
                          </>
                        )}
                      </div>
                    </div>

                    {expandedId === role.roleId && canExpand(role) && (
                      <RolePermissionsPanel role={role} isSystemRole={!!role.isSystemRole} />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {showCreate && <RoleFormModal onClose={() => setShowCreate(false)} />}
        {editingRole && <RoleFormModal role={editingRole} onClose={() => setEditingRole(null)} />}
      </div>
    </PageGuard>
  );
};
